(function () {
  'use strict';

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function normalizeUrl(value) {
    var trimmed = String(value || '').trim();
    if (!trimmed) return '';
    if (trimmed.indexOf('//') === 0) return 'https:' + trimmed;
    if (/^www\./i.test(trimmed)) return 'https://' + trimmed;
    return trimmed;
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\[\]^$*+?.(){}|]/g, '\\$&');
  }

  // Default no-op teardown sink. Zero-config hosts (the landing renderer and
  // the builder preview) call enhance(root) with no register, so teardown is
  // registered but never invoked — those pages never swap content. A host that
  // re-renders in place (the storefront snippet shell) passes a real register
  // and drives cleanup on variant swap.
  function noopRegister() {}

  // The scene-claim set arrives as a Set or an array (storefront host) or is
  // absent (zero-config host). Normalize to a Set, or null when absent so
  // isClaimed can fall back to the DOM scene-wrapper signal.
  function normalizeClaimedBlockIds(value) {
    if (!value) return null;
    if (typeof value.has === 'function') return value;
    var set = new Set();
    for (var i = 0; i < value.length; i++) set.add(value[i]);
    return set;
  }

  // A module is scene-claimed when a cross-block motion scene owns its
  // scroll/entrance behavior; the legacy per-module init must then not also
  // drive it (one driver per element). Two signals, by host: with a claim set
  // (storefront), a module is claimed iff its own-or-ancestor
  // data-aplus-section-id is in the set; with no set (landing/preview), the DOM
  // scene wrapper [data-sp-motion-scene] is the signal.
  function isClaimed(el, claimedBlockIds) {
    if (claimedBlockIds) {
      if (claimedBlockIds.size === 0) return false;
      var sectionRoot = el.closest('[data-aplus-section-id]');
      var blockId = sectionRoot ? sectionRoot.getAttribute('data-aplus-section-id') : null;
      return !!blockId && claimedBlockIds.has(blockId);
    }
    return !!el.closest('[data-sp-motion-scene]');
  }

  // Collect addEventListener wiring so a scroll/observer init can be fully
  // removed on teardown (storefront variant swap). removeEventListener matches
  // on the same handler + options object, so pass opts straight back through.
  function bindListeners() {
    var removers = [];
    return {
      on: function (target, type, handler, opts) {
        target.addEventListener(type, handler, opts);
        removers.push(function () { target.removeEventListener(type, handler, opts); });
      },
      teardown: function () {
        for (var i = 0; i < removers.length; i++) {
          try { removers[i](); } catch (error) {}
        }
        removers.length = 0;
      }
    };
  }

  function canAutoplayInlineVideo(module) {
    if (!module.isConnected) return false;
    if (module.closest('[hidden],[aria-hidden="true"]')) return false;
    var scrollPanel = module.closest('.spectrum-aplus__scroll-selector-panel');
    if (scrollPanel && !scrollPanel.classList.contains('spectrum-aplus__scroll-selector-panel--active')) {
      return false;
    }
    return true;
  }

  function initInlineVideos(root, register) {
    root.querySelectorAll('[data-aplus-module="inline-video"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var video = module.querySelector('video');
      if (!video) return;
      var mute = module.querySelector('[data-aplus-action="mute"]');

      var src = normalizeUrl(video.getAttribute('src'));
      var sourceChanged = false;
      if (src && src !== video.getAttribute('src')) {
        video.setAttribute('src', src);
        sourceChanged = true;
      }
      var poster = normalizeUrl(video.getAttribute('poster'));
      if (poster && poster !== video.getAttribute('poster')) video.setAttribute('poster', poster);
      video.querySelectorAll('source[src]').forEach(function (source) {
        var sourceSrc = normalizeUrl(source.getAttribute('src'));
        if (sourceSrc && sourceSrc !== source.getAttribute('src')) {
          source.setAttribute('src', sourceSrc);
          sourceChanged = true;
        }
      });
      var mobileSrc = normalizeUrl(video.getAttribute('data-aplus-mobile-src'));
      // Autoplay intent arrives as data-aplus-video-autoplay="true"
      // (compiler-emitted; the native attribute would make the browser fetch
      // video bytes at insertion, before this observer can gate it). Stored
      // HTML compiled before the data-attribute switch still carries the
      // native attribute: convert it losslessly — set the data attribute,
      // then strip the native one — so a re-enhance or clone keeps the
      // autoplay intent without ever handing the browser an eager download
      // the observer below is supposed to gate.
      if (video.hasAttribute('autoplay')) {
        // An explicit data attribute wins over the legacy native one — never
        // overwrite an authored "false" during conversion.
        if (!video.hasAttribute('data-aplus-video-autoplay')) {
          video.setAttribute('data-aplus-video-autoplay', 'true');
        }
        video.removeAttribute('autoplay');
      }
      var autoplay = video.getAttribute('data-aplus-video-autoplay') === 'true';

      // Arming = mobile-src swap + upgrade preload metadata -> auto + load(). The
      // SSR ships preload="metadata" to keep first paint cheap, so this upgrade
      // is what pulls the full video bytes. DEFERRED until the card nears the
      // viewport (observer below) so below-the-fold A+ videos stay off the
      // first-load payload — gating the byte download, not just playback, is the
      // whole point. Idempotent.
      var mediaArmed = false;
      function armMedia() {
        if (mediaArmed) return;
        mediaArmed = true;
        var mobileSwapped = false;
        if (mobileSrc && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) {
          video.src = mobileSrc;
          mobileSwapped = true;
        }
        if (autoplay) {
          video.muted = true;
          video.preload = 'auto';
        }
        // Only re-run resource selection when something actually changed — the
        // autoplay preload upgrade, a mobile-src swap, or a normalized
        // src/source URL. A bare load() on an unchanged video restarts an
        // in-flight metadata fetch and can flash the poster.
        if ((autoplay || mobileSwapped || sourceChanged) && video.load) video.load();
      }
      // Expose arming so the carousel's setActive() can arm a not-yet-observed
      // card before force-playing it. A direct dot/arrow nav to a card still
      // >600px off-screen fires setActive's play() before that card's own
      // observer runs armMedia — without this, play() starts the unswapped
      // (desktop) source and the later arm's load() interrupts it.
      video.saiArm = armMedia;
      // Play once armed. Cloned videos don't honor the autoplay attribute, and
      // play() right after load() rejects at readyState 0, so wait for
      // loadeddata/canplay with a short fallback. Safe to re-call.
      function tryPlay() {
        if (!autoplay || !canAutoplayInlineVideo(module)) return;
        var kickPlay = function () {
          if (canAutoplayInlineVideo(module) && video.play) video.play().catch(function () {});
        };
        if (video.readyState >= 2) {
          kickPlay();
        } else {
          video.addEventListener('loadeddata', kickPlay, { once: true });
          video.addEventListener('canplay', kickPlay, { once: true });
          window.setTimeout(kickPlay, 600);
        }
      }

      if (mute) {
        var syncMute = function () {
          mute.setAttribute('aria-pressed', String(!video.muted));
          mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
          mute.setAttribute('data-aplus-muted', String(video.muted));
        };
        syncMute();
        var onMuteClick = function (event) {
          event.preventDefault();
          event.stopPropagation();
          video.muted = !video.muted;
          syncMute();
          if (video.paused && video.play) video.play().catch(function () {});
        };
        mute.addEventListener('click', onMuteClick);
        if (register) register(function () { mute.removeEventListener('click', onMuteClick); });
      }

      // Defer both the byte-download (armMedia) and playback to ~600px before the
      // card enters view; pause when it leaves so off-screen A+ videos neither
      // download nor keep playing. tryPlay() no-ops on aria-hidden carousel cards
      // (canAutoplayInlineVideo), so only the active card ever plays.
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              armMedia();
              tryPlay();
            } else if (video.pause) {
              video.pause();
            }
          });
        }, { threshold: 0, rootMargin: '600px 0px' });
        observer.observe(module);
        if (register) register(function () {
          observer.disconnect();
          if (video.pause) video.pause();
        });
      } else {
        armMedia();
        tryPlay();
      }
    });
  }

  function initHighlightsCarousels(root, register) {
    var useReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-aplus-module="highlights-carousel"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';
      var binder = bindListeners();

      var track = module.querySelector('.spectrum-aplus__highlights-track');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('.spectrum-aplus__highlights-card'));
      var dots = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-action="carousel-dot"]'));
      var toggle = module.querySelector('[data-aplus-action="carousel-toggle"]');
      var prevArrow = module.querySelector('[data-aplus-action="carousel-prev"]');
      var nextArrow = module.querySelector('[data-aplus-action="carousel-next"]');
      var arrowsWrap = module.querySelector('.spectrum-aplus__highlights-arrows');
      if (!slides.length) return;
      // Near-viewport gate for setActive()'s video playback (see the view
      // observer at the end of this init). Off-screen carousels don't pull their
      // active video onto the first-load payload until they approach the view.
      var carouselInView = false;

      var activeIndex = 0;
      var timer = null;
      var scrollFrame = 0;
      var paused = false;
      // Guards a click-driven programmatic scroll so the scroll listener does
      // not re-derive activeIndex mid-animation and fight the click.
      var programmaticScroll = false;
      var programmaticTimer = null;
      var autoplay = module.getAttribute('data-aplus-autoplay') === 'true' && !useReducedMotion;
      var interval = Math.max(2000, Number(module.getAttribute('data-aplus-interval') || 5000));

      function clearTimer() {
        if (!timer) return;
        window.clearTimeout(timer);
        timer = null;
      }

      function restartProgressAnimation() {
        module.querySelectorAll('.spectrum-aplus__highlights-dot-progress').forEach(function (progress) {
          progress.style.animation = 'none';
          progress.style.transition = 'none';
          progress.style.transform = 'scaleX(0)';
          void progress.offsetWidth;
          progress.style.animation = '';
          progress.style.transition = '';
        });
        var activeDot = dots[activeIndex];
        var activeProgress = activeDot ? activeDot.querySelector('.spectrum-aplus__highlights-dot-progress') : null;
        if (activeProgress && autoplay && !paused) {
          activeProgress.style.animation = 'none';
          activeProgress.style.transition = 'none';
          activeProgress.style.transform = 'scaleX(0)';
          void activeProgress.offsetWidth;
          activeProgress.style.transition = 'transform ' + interval + 'ms linear';
          activeProgress.style.transform = 'scaleX(1)';
        }
      }

      function setPaused(nextPaused) {
        paused = nextPaused;
        module.setAttribute('data-aplus-paused', String(paused));
        if (toggle) {
          toggle.setAttribute('aria-pressed', String(!paused));
          toggle.setAttribute('aria-label', paused ? 'Play highlights autoplay' : 'Pause highlights autoplay');
        }
        restartProgressAnimation();
      }

      function setActive(nextIndex, shouldScroll) {
        activeIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
        module.setAttribute('data-aplus-active', String(activeIndex));
        slides.forEach(function (slide, index) {
          var active = index === activeIndex;
          slide.classList.toggle('spectrum-aplus__highlights-card--active', active);
          slide.setAttribute('aria-hidden', String(!active));
          // Manually drive playback on slide change. The inline-video
          // IntersectionObserver only fires when geometry changes; flipping
          // aria-hidden doesn't refire it, so without this the video stays
          // paused for the lifetime of the carousel even after its card
          // becomes active. canAutoplayInlineVideo also short-circuits on
          // [aria-hidden="true"] ancestors, so the observer refuses to play a
          // freshly-activated card — manual play() is the only path that works.
          // Active-play is gated on carouselInView so the init-time setActive(0)
          // doesn't pull a below-the-fold carousel's video at mount; the pause
          // branch stays so a deactivated slide's video stops (no audio bleed).
          slide.querySelectorAll('video').forEach(function (video) {
            if (active) {
              if (carouselInView && video.play) {
                if (video.saiArm) video.saiArm();
                video.play().catch(function () {});
              }
            } else if (video.pause) {
              video.pause();
            }
          });
          // YouTube / Vimeo are iframes — postMessage to each provider's
          // JS API so non-active slides don't keep playing audio.
          slide.querySelectorAll('iframe[data-video-source], figure[data-video-source] iframe').forEach(function (iframe) {
            var source = iframe.getAttribute('data-video-source');
            if (!source) {
              var ancestor = iframe.closest('[data-video-source]');
              source = ancestor ? ancestor.getAttribute('data-video-source') : null;
            }
            var win = iframe.contentWindow;
            if (!source || !win) return;
            if (source === 'youtube') {
              win.postMessage(JSON.stringify({ event: 'command', func: active ? 'playVideo' : 'pauseVideo', args: [] }), '*');
            } else if (source === 'vimeo') {
              win.postMessage(JSON.stringify({ method: active ? 'play' : 'pause' }), '*');
            }
          });
        });
        dots.forEach(function (dot, index) {
          var active = index === activeIndex;
          dot.classList.toggle('spectrum-aplus__highlights-dot--active', active);
          dot.setAttribute('aria-pressed', String(active));
        });
        // Hide the arrows entirely when the track has nothing to scroll (all
        // cards already fit in the viewport); when it does overflow, show them
        // and disable only at the respective ends. Assigning '' (rather than a
        // fixed display value) defers to the stylesheet, so the mobile media
        // query that hides the header arrows still wins.
        var hasOverflow = track.scrollWidth - track.clientWidth > 4;
        if (arrowsWrap) arrowsWrap.style.display = hasOverflow ? '' : 'none';
        if (prevArrow) prevArrow.disabled = activeIndex <= 0;
        if (nextArrow) nextArrow.disabled = activeIndex >= slides.length - 1;
        if (shouldScroll) {
          var slide = slides[activeIndex];
          // Lock scroll reconciliation for the duration of this programmatic
          // scroll so a burst of prev/next clicks steps deterministically
          // instead of the scroll listener re-deriving activeIndex mid-flight.
          programmaticScroll = true;
          if (programmaticTimer) window.clearTimeout(programmaticTimer);
          programmaticTimer = window.setTimeout(function () {
            programmaticScroll = false;
            programmaticTimer = null;
          }, 450);
          track.scrollTo({
            left: slide.offsetLeft - track.offsetLeft,
            behavior: useReducedMotion ? 'auto' : 'smooth',
          });
        }
        restartProgressAnimation();
      }

      function restartTimer() {
        clearTimer();
        if (!autoplay || paused || slides.length < 2) return;
        timer = window.setTimeout(function () {
          setActive((activeIndex + 1) % slides.length, true);
          restartTimer();
        }, interval);
      }

      dots.forEach(function (dot) {
        binder.on(dot, 'click', function () {
          var index = Number(dot.getAttribute('data-aplus-index') || 0);
          setActive(index, true);
          setPaused(false);
          restartTimer();
        });
      });

      if (toggle) {
        binder.on(toggle, 'click', function () {
          setPaused(!paused);
          restartTimer();
        });
      }

      if (prevArrow) {
        binder.on(prevArrow, 'click', function () {
          setActive(activeIndex - 1, true);
          setPaused(false);
          restartTimer();
        });
      }

      if (nextArrow) {
        binder.on(nextArrow, 'click', function () {
          setActive(activeIndex + 1, true);
          setPaused(false);
          restartTimer();
        });
      }

      binder.on(track, 'scroll', function () {
        if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(function () {
          scrollFrame = 0;
          if (programmaticScroll) return;
          var nextIndex = activeIndex;
          var closestDistance = Math.abs(slides[activeIndex].offsetLeft - track.scrollLeft - track.offsetLeft);
          slides.forEach(function (slide, index) {
            var distance = Math.abs(slide.offsetLeft - track.scrollLeft - track.offsetLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              nextIndex = index;
            }
          });
          if (nextIndex !== activeIndex) {
            setActive(nextIndex, false);
            restartTimer();
          }
        });
      }, { passive: true });
      // Recompute the arrow disabled state on resize — overflow (and therefore
      // whether the arrows should be usable) depends on the viewport width.
      binder.on(window, 'resize', function () { setActive(activeIndex, false); }, { passive: true });
      if (register) register(function () {
        binder.teardown();
        clearTimer();
        if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
        if (programmaticTimer) window.clearTimeout(programmaticTimer);
      });

      // Flip carouselInView when the carousel nears the viewport and replay the
      // active slide, so a below-the-fold carousel's active video is fetched on
      // approach rather than at mount. No IntersectionObserver -> assume in view.
      if ('IntersectionObserver' in window) {
        var viewObserver = new IntersectionObserver(function (entries) {
          var nowInView = entries[entries.length - 1].isIntersecting;
          if (nowInView === carouselInView) return;
          carouselInView = nowInView;
          setActive(activeIndex, false);
        }, { rootMargin: '200px 0px' });
        viewObserver.observe(module);
        if (register) register(function () { viewObserver.disconnect(); });
      } else {
        carouselInView = true;
      }

      setPaused(false);
      setActive(0, false);
      restartTimer();
    });
  }

  function setActivePanel(module, index) {
    module.querySelectorAll('[data-aplus-panel]').forEach(function (panel) {
      var active = panel.getAttribute('data-aplus-panel') === String(index);
      panel.toggleAttribute('hidden', !active);
      var firstClass = String(panel.classList[0] || '');
      if (firstClass) panel.classList.toggle(firstClass + '--active', active);
      panel.setAttribute('aria-hidden', String(!active));
      panel.querySelectorAll('video').forEach(function (video) {
        if (active) video.play && video.play().catch(function () {});
        else video.pause && video.pause();
      });
    });
  }

  function initTabbedMedia(root, register) {
    root.querySelectorAll('[data-aplus-module="tabbed-media-explorer"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';
      var binder = bindListeners();

      var groups = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-tab-group]'));
      var panels = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-combination]'));
      var selected = new Map();

      function update() {
        var visibleGroups = groups.filter(function (group) {
          var parentTab = group.getAttribute('data-aplus-parent-tab');
          var selectedValues = Array.from(selected.values());
          var visible = !parentTab || selectedValues.indexOf(parentTab) >= 0;
          group.toggleAttribute('hidden', !visible);
          return visible;
        });

        var nestedRef = null, topRef = null;
        visibleGroups.forEach(function (group) {
          var groupId = group.getAttribute('data-aplus-tab-group');
          var isNested = !!group.getAttribute('data-aplus-parent-tab');
          var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-aplus-tab]'));
          var current = selected.get(groupId);
          var activeTab = tabs.find(function (tab) {
            return tab.getAttribute('data-aplus-tab') === current;
          }) || (isNested ? tabs[0] : null);
          if (activeTab) selected.set(groupId, activeTab.getAttribute('data-aplus-tab'));
          else selected.delete(groupId);
          if (activeTab) {
            var ref = activeTab.getAttribute('data-aplus-media-ref');
            if (ref) { if (isNested) nestedRef = ref; else topRef = ref; }
          }
          tabs.forEach(function (tab) {
            var active = tab === activeTab;
            tab.classList.toggle('spectrum-aplus__tabbed-media-tab--active', active);
            tab.setAttribute('aria-pressed', String(active));
          });
        });

        // Pulsar single-media-swap model: a selected tab's mediaRef points
        // straight at one panel by id; the deepest (nested) selection wins.
        // Falls back to tabIds-combination matching when no mediaRef is set.
        var controllingRef = nestedRef || topRef;
        var refPanel = controllingRef
          ? module.querySelector('[data-aplus-panel-id="' + cssEscape(controllingRef) + '"]')
          : null;
        if (refPanel) {
          setActivePanel(module, refPanel.getAttribute('data-aplus-panel') || '0');
          return;
        }

        var activeIds = visibleGroups.map(function (group) {
          return selected.get(group.getAttribute('data-aplus-tab-group'));
        }).filter(Boolean);
        var combination = activeIds.join('|');
        var exact = module.querySelector('[data-aplus-combination="' + cssEscape(combination) + '"]');
        var subset = panels.find(function (candidate) {
          var ids = String(candidate.getAttribute('data-aplus-combination') || '').split('|').filter(Boolean);
          return ids.length > 0 && ids.every(function (id) { return activeIds.indexOf(id) >= 0; });
        });
        var panel = exact || subset || panels[0];
        setActivePanel(module, panel ? panel.getAttribute('data-aplus-panel') || '0' : '0');
      }

      var firstTopLevelGroup = groups.find(function (group) {
        return !group.getAttribute('data-aplus-parent-tab');
      });
      if (firstTopLevelGroup) {
        var firstTab = firstTopLevelGroup.querySelector('[data-aplus-tab]');
        if (firstTab) {
          selected.set(
            firstTopLevelGroup.getAttribute('data-aplus-tab-group'),
            firstTab.getAttribute('data-aplus-tab'),
          );
        }
      }
      groups.forEach(function (group) {
        var groupId = group.getAttribute('data-aplus-tab-group');
        var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-aplus-tab]'));
        tabs.forEach(function (tab) {
          binder.on(tab, 'click', function () {
            selected.set(groupId, tab.getAttribute('data-aplus-tab'));
            if (window.innerWidth <= 768 && tab.parentElement && tab.parentElement.scrollTo) {
              tab.parentElement.scrollTo({
                left: tab.offsetLeft - tab.parentElement.offsetWidth / 2 + tab.offsetWidth / 2,
                behavior: 'smooth'
              });
            }
            update();
          });
        });
      });
      if (register) register(function () { binder.teardown(); });
      // Client-rendered media (storefront host) isn't pre-normalized; normalize
      // src on img/source/video after wiring. Idempotent on the landing host,
      // whose media already carries absolute URLs.
      module.querySelectorAll('img[src], source[src], video[src]').forEach(function (media) {
        var attr = media.getAttribute('src');
        var normalized = normalizeUrl(attr);
        if (normalized && normalized !== attr) media.setAttribute('src', normalized);
      });
      update();
    });
  }

  function initScrollFeatureSelectors(root, register, claimedBlockIds) {
    root.querySelectorAll('[data-aplus-module="scroll-feature-selector"]').forEach(function (module) {
      if (isClaimed(module, claimedBlockIds)) return;
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var items = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-index]'));
      var panels = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-panel]'));
      if (!items.length) return;
      var interaction = module.getAttribute('data-aplus-interaction') || 'pinned-scroll';
      var titleArea = module.querySelector('.spectrum-aplus__scroll-selector-title-area');
      var progressFill = module.querySelector('.spectrum-aplus__scroll-selector-progress-fill');
      var counter = module.querySelector('.spectrum-aplus__scroll-selector-counter-current');
      var activeIndex = Number(module.getAttribute('data-aplus-active') || '0');
      var lastWheelAt = 0;
      var ticking = false;
      var animationFrame = 0;
      var binder = bindListeners();

      function progressAxis() {
        return window.matchMedia && window.matchMedia('(max-width: 768px)').matches ? 'width' : 'height';
      }

      function setProgress(progress) {
        if (!progressFill) return;
        var safeProgress = Math.max(0, Math.min(1, progress));
        if (progressAxis() === 'width') {
          progressFill.style.width = (safeProgress * 100) + '%';
          progressFill.style.height = '100%';
        } else {
          progressFill.style.height = (safeProgress * 100) + '%';
          progressFill.style.width = '100%';
        }
      }

      function syncVideos() {
        panels.forEach(function (panel) {
          var isActive = panel.getAttribute('data-aplus-panel') === String(activeIndex);
          panel.querySelectorAll('video').forEach(function (video) {
            if (isActive) video.play && video.play().catch(function () {});
            else video.pause && video.pause();
          });
        });
      }

      function centerActiveTitle() {
        if (!titleArea || progressAxis() !== 'width') return;
        var activeItem = items[activeIndex];
        if (!activeItem) return;
        var itemCenter = activeItem.offsetLeft + activeItem.offsetWidth / 2;
        var targetLeft = Math.max(0, itemCenter - window.innerWidth / 2);
        if (titleArea.scrollTo) titleArea.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      function update(nextIndex, progress) {
        activeIndex = Math.max(0, Math.min(items.length - 1, nextIndex));
        module.setAttribute('data-aplus-active', String(activeIndex));
        items.forEach(function (item) {
          var isActive = item.getAttribute('data-aplus-index') === String(activeIndex);
          var shell = item.closest('.spectrum-aplus__scroll-selector-item');
          if (shell) shell.classList.toggle('spectrum-aplus__scroll-selector-item--active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
        });
        panels.forEach(function (panel) {
          var isActive = panel.getAttribute('data-aplus-panel') === String(activeIndex);
          panel.classList.toggle('spectrum-aplus__scroll-selector-panel--active', isActive);
          panel.setAttribute('aria-hidden', String(!isActive));
        });
        if (counter) counter.textContent = String(activeIndex + 1);
        setProgress(typeof progress === 'number' ? progress : (items.length > 1 ? activeIndex / (items.length - 1) : 1));
        centerActiveTitle();
        syncVideos();
        // The active panel's inline videos may have been swapped in with the
        // panel; (re-)enhance them each update so a newly-shown video autoplays.
        // Idempotent — already-enhanced modules short-circuit.
        initInlineVideos(module, register);
      }

      items.forEach(function (item) {
        binder.on(item, 'click', function () {
          var nextIndex = Number(item.getAttribute('data-aplus-index') || '0');
          if (interaction !== 'pinned-scroll') {
            update(nextIndex);
            return;
          }
          var scrollDistance = Math.max(1, module.offsetHeight - window.innerHeight);
          var targetProgress = items.length > 1 ? nextIndex / (items.length - 1) : 0;
          window.scrollTo({
            top: window.scrollY + module.getBoundingClientRect().top + scrollDistance * targetProgress,
            behavior: 'smooth'
          });
        });
      });

      function scrollUpdate() {
        ticking = false;
        if (!module.isConnected) return;
        var rect = module.getBoundingClientRect();
        var scrollDistance = Math.max(1, module.offsetHeight - window.innerHeight);
        var progress = Math.max(0, Math.min(1, rect.top <= 0 ? Math.abs(rect.top) / scrollDistance : 0));
        var nextIndex = Math.min(items.length - 1, Math.round(progress * (items.length - 1)));
        update(nextIndex, progress);
      }

      function requestScrollUpdate() {
        if (ticking) return;
        ticking = true;
        animationFrame = window.requestAnimationFrame(scrollUpdate);
      }

      if (interaction === 'pinned-scroll') {
        binder.on(window, 'scroll', requestScrollUpdate, { passive: true });
        binder.on(window, 'resize', requestScrollUpdate);
        requestScrollUpdate();
      } else {
        binder.on(module, 'wheel', function (event) {
          var now = Date.now();
          if (Math.abs(event.deltaY) < 24 || now - lastWheelAt < 280) return;
          lastWheelAt = now;
          update(activeIndex + (event.deltaY > 0 ? 1 : -1));
        }, { passive: true });
        update(activeIndex);
      }
      if (register) register(function () {
        binder.teardown();
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
      });
    });
  }

  function initScrollRevealSequences(root, register, claimedBlockIds) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-aplus-module="scroll-reveal-sequence"]').forEach(function (module) {
      if (isClaimed(module, claimedBlockIds)) return;
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';
      if (reduced) return;

      function explicitFrames(attrName) {
        return String(module.getAttribute(attrName) || '').split('|').map(normalizeUrl).filter(Boolean);
      }
      function patternFrames(prefix) {
        var baseUrl = normalizeUrl(module.getAttribute('data-aplus-' + prefix + '-frame-prefix'));
        var extension = String(module.getAttribute('data-aplus-' + prefix + '-frame-extension') || '').trim().replace(/^\./, '');
        var count = Number.parseInt(module.getAttribute('data-aplus-' + prefix + '-frame-count') || '0', 10);
        var start = Number.parseInt(module.getAttribute('data-aplus-' + prefix + '-frame-start') || '1', 10);
        var pad = Number.parseInt(module.getAttribute('data-aplus-' + prefix + '-frame-pad') || '4', 10);
        if (!baseUrl || !/^[a-z0-9]{2,8}$/i.test(extension) || count < 2 || count > 240) return [];
        return Array.from({ length: count }, function (_, index) {
          return baseUrl + String(start + index).padStart(Math.max(1, Math.min(6, pad)), '0') + '.' + extension;
        });
      }

      var desktopFrames = explicitFrames('data-aplus-frame-srcs');
      var desktopPatternFrames = patternFrames('desktop');
      var mobileFrames = explicitFrames('data-aplus-mobile-frame-srcs');
      var mobilePatternFrames = patternFrames('mobile');
      var desktopFrameSet = desktopFrames.length >= 2 ? desktopFrames : desktopPatternFrames;
      var mobileFrameSet = mobileFrames.length >= 2 ? mobileFrames : mobilePatternFrames;

      var img = module.querySelector('img');
      var canvas = module.querySelector('canvas');
      var context = module.getAttribute('data-aplus-render-mode') === 'canvas' && canvas ? canvas.getContext('2d') : null;
      if ((!img && !context) || desktopFrameSet.length < 2) return;
      // Only switch to the JS pin model once we know the sequence is valid —
      // adding --scrub earlier would take the stage out of flow (CSS) with no
      // scroll handler if we bail on an invalid frame set, freezing it.
      module.classList.add('spectrum-aplus__scroll-sequence--scrub');
      var stage = module.querySelector('.spectrum-aplus__scroll-sequence-stage');

      var mobileQuery = window.matchMedia && window.matchMedia('(max-width: 640px)');
      var frames = mobileQuery && mobileQuery.matches && mobileFrameSet.length >= 2 ? mobileFrameSet : desktopFrameSet;
      var activeFrame = -1;
      var raf = 0;
      var preloadTimer = 0;
      var visible = false;
      var destroyed = false;
      var imageCache = new Map();

      function drawCanvasFrame(frame) {
        if (!canvas || !context) return;
        var width = frame.naturalWidth || frame.width || 1920;
        var height = frame.naturalHeight || frame.height || 1080;
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
        context.clearRect(0, 0, width, height);
        context.drawImage(frame, 0, 0, width, height);
        module.classList.add('spectrum-aplus__scroll-sequence--canvas-ready');
      }

      function loadFrame(src, index, onLoad) {
        var frame = imageCache.get(src);
        if (!frame) {
          frame = new Image();
          imageCache.set(src, frame);
          frame.src = src;
        }
        if (frame.complete && frame.naturalWidth) {
          if (!destroyed && index === activeFrame && frames[activeFrame] === src) onLoad(frame);
          return;
        }
        frame.onload = function () {
          if (!destroyed && index === activeFrame && frames[activeFrame] === src) onLoad(frame);
        };
        frame.onerror = function () {
          imageCache.delete(src);
        };
      }

      function preloadNearbyFrames() {
        var start = Math.max(0, activeFrame - 2);
        var end = Math.min(frames.length - 1, activeFrame + 3);
        frames.slice(start, end + 1).forEach(function (src) {
          if (imageCache.has(src)) return;
          var preloaded = new Image();
          imageCache.set(src, preloaded);
          preloaded.onerror = function () { imageCache.delete(src); };
          preloaded.src = src;
        });
      }

      function updateFrame() {
        if (destroyed || !module.isConnected) return;
        applyScrollPin(module, stage);
        if (!visible) return;
        var rect = module.getBoundingClientRect();
        var scrollable = Math.max(1, rect.height - window.innerHeight);
        var progress = Math.min(1, Math.max(0, -rect.top / scrollable));
        var frameIndex = Math.min(frames.length - 1, Math.max(0, Math.round(progress * (frames.length - 1))));
        if (frameIndex === activeFrame) return;
        activeFrame = frameIndex;
        var frameSrc = frames[frameIndex];
        if (context) loadFrame(frameSrc, frameIndex, drawCanvasFrame);
        else if (img && img.getAttribute('src') !== frameSrc) img.setAttribute('src', frameSrc);
        if (preloadTimer) window.clearTimeout(preloadTimer);
        preloadTimer = window.setTimeout(preloadNearbyFrames, 80);
      }

      function requestUpdate() {
        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          raf = 0;
          updateFrame();
        });
      }

      function updateFrameSet() {
        frames = mobileQuery && mobileQuery.matches && mobileFrameSet.length >= 2 ? mobileFrameSet : desktopFrameSet;
        activeFrame = -1;
        requestUpdate();
      }

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      if (mobileQuery && mobileQuery.addEventListener) mobileQuery.addEventListener('change', updateFrameSet);

      // Don't start scrubbing frames until the sequence is near the viewport —
      // a long page with several sequences would otherwise decode every frame
      // set on load. applyScrollPin still runs every frame (cheap); the frame
      // draw is gated on the visible flag.
      var visibilityObserver = null;
      function startSequence() {
        if (visible) return;
        visible = true;
        updateFrameSet();
      }
      if ('IntersectionObserver' in window) {
        visibilityObserver = new IntersectionObserver(function (entries) {
          if (entries.some(function (entry) { return entry.isIntersecting; })) startSequence();
        }, { rootMargin: '400px 0px' });
        visibilityObserver.observe(module);
      } else {
        startSequence();
      }
      if (register) register(function () {
        destroyed = true;
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
        if (mobileQuery && mobileQuery.removeEventListener) mobileQuery.removeEventListener('change', updateFrameSet);
        if (visibilityObserver) visibilityObserver.disconnect();
        if (raf) window.cancelAnimationFrame(raf);
        if (preloadTimer) window.clearTimeout(preloadTimer);
        imageCache.forEach(function (frame) { frame.onload = null; frame.onerror = null; });
        imageCache.clear();
      });
    });
  }

  function regexEscape(value) {
    return String(value).replace(/[.*+?^$\{\}()|[\]\\]/g, '\\$&');
  }

  // Pin a child within a tall track using fixed-positioning toggled by scroll
  // state, instead of CSS position:sticky. Sticky breaks when any ancestor has
  // overflow != visible (a near-universal theme pattern on mobile); fixed is
  // only affected by transform/filter ancestors, so this survives arbitrary
  // merchant themes. The track keeps its height (the pinned child is taken out
  // of flow via the --scrub CSS), so scroll math is unaffected.
  function applyScrollPin(track, pinned) {
    if (!pinned) return;
    var rect = track.getBoundingClientRect();
    var ph = pinned.offsetHeight || window.innerHeight;
    var mode = rect.top > 0 ? 'before' : (rect.bottom > ph ? 'pinned' : 'after');
    if (pinned.getAttribute('data-aplus-pin') !== mode) pinned.setAttribute('data-aplus-pin', mode);
    if (mode === 'pinned') {
      // Guard the writes: a pinned frame with unchanged geometry then costs one
      // getBoundingClientRect read and no style mutation, so no layout thrash.
      var left = rect.left + 'px';
      var width = rect.width + 'px';
      if (pinned.style.left !== left) pinned.style.left = left;
      if (pinned.style.width !== width) pinned.style.width = width;
    } else if (pinned.style.left) {
      pinned.style.left = '';
      pinned.style.width = '';
    }
  }

  function initScrollTextReveals(root, register, claimedBlockIds) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-aplus-module="scroll-text-reveal"]').forEach(function (module) {
      if (isClaimed(module, claimedBlockIds)) return;
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var textElement = module.querySelector('[data-aplus-scroll-text]');
      var track = module.querySelector('.spectrum-aplus__scroll-text-track');
      var sticky = module.querySelector('.spectrum-aplus__scroll-text-sticky');
      if (!textElement || !track) return;

      var originalText = String(textElement.textContent || '').trim();
      if (!originalText) return;
      var highlightPhrases = [];
      try {
        var parsed = JSON.parse(module.getAttribute('data-aplus-highlight-phrases') || '[]');
        if (Array.isArray(parsed)) highlightPhrases = parsed.map(String).filter(Boolean);
      } catch (error) {}
      var revealRange = Math.max(0.03, Math.min(0.3, Number(module.getAttribute('data-aplus-reveal-range') || 0.07)));

      textElement.textContent = '';
      textElement.setAttribute('aria-label', originalText);
      var markedText = originalText;
      highlightPhrases.slice().sort(function (a, b) { return b.length - a.length; }).forEach(function (phrase, index) {
        // Case-insensitive so phrases entered in display case (the copy renders
        // text-transform:uppercase) still match the stored mixed-case text; the
        // callback preserves the original matched casing.
        markedText = markedText.replace(new RegExp(regexEscape(phrase), 'gi'), function (match) {
          return '[[HL' + index + ']]' + match + '[[END]]';
        });
      });

      var charElements = [];
      function createWordSpans(text, isHighlight) {
        var word = document.createElement('span');
        word.className = 'spectrum-aplus__scroll-text-word' + (isHighlight ? ' spectrum-aplus__scroll-text-word--highlight' : '');
        word.setAttribute('aria-hidden', 'true');
        Array.from(text).forEach(function (char) {
          var span = document.createElement('span');
          span.className = 'spectrum-aplus__scroll-text-char';
          span.textContent = char;
          word.append(span);
          charElements.push(span);
        });
        textElement.append(word);
      }

      markedText.split(/(\[\[HL\d+\]\].+?\[\[END\]\]|\s+)/).forEach(function (token) {
        if (!token) return;
        if (/^\s+$/.test(token)) {
          textElement.append(document.createTextNode(token));
          return;
        }
        if (token.indexOf('[[HL') === 0) {
          token.replace(/\[\[HL\d+\]\]|\[\[END\]\]/g, '').split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) textElement.append(document.createTextNode(part));
            else createWordSpans(part, true);
          });
          return;
        }
        createWordSpans(token, false);
      });

      function update() {
        applyScrollPin(track, sticky);
        var rect = track.getBoundingClientRect();
        var distance = Math.max(1, track.offsetHeight - window.innerHeight);
        var progress = Math.max(0, Math.min(1, rect.top <= 0 ? Math.abs(rect.top) / distance : 0));
        var total = Math.max(1, charElements.length);
        charElements.forEach(function (char, index) {
          var threshold = index / total * (1 - revealRange);
          var effect = Math.max(0, Math.min(1, (progress - threshold) / revealRange));
          char.style.opacity = effect >= 0.98 ? '1' : String(0.05 + effect * 0.95);
          char.style.transform = 'translateY(' + (3 - effect * 3) + 'px)';
        });
      }

      if (reduced) {
        charElements.forEach(function (char) {
          char.style.opacity = '1';
          char.style.transform = 'none';
        });
        return;
      }

      module.classList.add('spectrum-aplus__scroll-text--scrub');
      var ticking = false;
      var animationFrame = 0;
      function requestUpdate() {
        if (ticking) return;
        ticking = true;
        animationFrame = window.requestAnimationFrame(function () {
          ticking = false;
          animationFrame = 0;
          update();
        });
      }
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      if (register) register(function () {
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
      });
      update();
    });
  }

  function initTechnicalSpecsAccordions(root) {
    root.querySelectorAll('[data-aplus-module="technical-specs-accordion"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';
      var groups = Array.prototype.slice.call(module.querySelectorAll('details'));
      var expandAll = module.querySelector('[data-aplus-action="expand-all"]');
      if (!expandAll || !groups.length) return;
      function sync() {
        var allOpen = groups.every(function (group) { return group.open; });
        expandAll.setAttribute('aria-pressed', String(allOpen));
        expandAll.setAttribute('aria-expanded', String(allOpen));
      }
      expandAll.addEventListener('click', function () {
        var shouldOpen = !groups.every(function (group) { return group.open; });
        groups.forEach(function (group) { group.open = shouldOpen; });
        sync();
      });
      groups.forEach(function (group) { group.addEventListener('toggle', sync); });
      sync();
    });
  }

  // ---- Commerce modules (buy-box, product-gallery, sticky-buy-bar) ----
  // These bind LIVE product data from the document's projection blob and post to
  // the cart. Live PDPs emit the blob from Liquid; builder/agent preview injects
  // a mock blob (preview:true) synthesized from the persisted destination
  // product. The runtime parses exactly one shape and never branches on
  // mock-vs-live except to disable real ATC posts when preview is true.

  // Read the single document-scoped product projection. Mirrors readEnvelope:
  // JSON.parse of the script tag's textContent in try/catch, null on failure.
  // One PDP = one product, so the lookup is document-wide.
  function readProductProjection() {
    var el = document.querySelector('script[data-spectrum-product]');
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent || '');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  // Loud degrade: warn ONCE per page, flag the module, leave the static
  // placeholder markup visible. Never throw, never fabricate data.
  var aplusProductWarned = false;
  function degradeCommerceModule(module, reason) {
    module.setAttribute('data-aplus-degraded', reason);
    if (!aplusProductWarned) {
      aplusProductWarned = true;
      if (window.console && window.console.warn) {
        window.console.warn('[spectrum-aplus] product projection ' + reason + ' — commerce modules render a static placeholder. Ensure the PDP wrapper emits <script data-spectrum-product>.');
      }
    }
  }

  // Resolve a variant from a position-keyed selection object ({1,2,3}) by strict
  // option-tuple match, with a best-available fallback scoring matched positions.
  // Copied from the vdc8x4kp contract.
  function resolveAplusVariant(projection, selection) {
    var variants = projection.variants || [];
    var i;
    for (i = 0; i < variants.length; i++) {
      var v = variants[i];
      var o2 = v.option2 == null ? null : v.option2;
      var o3 = v.option3 == null ? null : v.option3;
      var s2 = selection[2] == null ? null : selection[2];
      var s3 = selection[3] == null ? null : selection[3];
      if (v.option1 === selection[1] && o2 === s2 && o3 === s3) return v;
    }
    var best = null;
    var bestScore = -1;
    for (i = 0; i < variants.length; i++) {
      var cand = variants[i];
      if (!cand.available) continue;
      var score = 0;
      if (cand.option1 === selection[1]) score++;
      if ((cand.option2 == null ? null : cand.option2) === (selection[2] == null ? null : selection[2])) score++;
      if ((cand.option3 == null ? null : cand.option3) === (selection[3] == null ? null : selection[3])) score++;
      if (score > bestScore) { bestScore = score; best = cand; }
    }
    return best;
  }

  function findVariantById(projection, variantId) {
    var variants = projection.variants || [];
    var target = String(variantId);
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === target) return variants[i];
    }
    return null;
  }

  function currentVariantFromUrl(projection) {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get('variant');
    if (raw) {
      var byUrl = findVariantById(projection, raw);
      if (byUrl) return byUrl;
    }
    if (projection.currentVariantId != null) {
      var byCurrent = findVariantById(projection, projection.currentVariantId);
      if (byCurrent) return byCurrent;
    }
    var variants = projection.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants.length ? variants[0] : null;
  }

  // Window-guarded singleton history patch. All commerce modules register a
  // handler here so a ?variant= write from any of them (or the theme picker /
  // vdc8x4kp) re-syncs every module. Composes with sibling snippet patches:
  // each patch wraps the previously-installed function.
  function installAplusHistoryPatch() {
    if (window.__sai_aplus_history_patched__) return;
    window.__sai_aplus_history_patched__ = true;
    window.__sai_aplus_handlers__ = [];
    var origPush = history.pushState.bind(history);
    var origReplace = history.replaceState.bind(history);
    var fire = function () {
      var handlers = window.__sai_aplus_handlers__;
      for (var i = 0; i < handlers.length; i++) {
        try { handlers[i](); } catch (error) {}
      }
    };
    history.pushState = function () {
      origPush.apply(history, arguments);
      fire();
    };
    history.replaceState = function () {
      origReplace.apply(history, arguments);
      fire();
    };
    window.addEventListener('popstate', fire);
  }

  function registerAplusVariantHandler(handler, register) {
    installAplusHistoryPatch();
    (window.__sai_aplus_handlers__ = window.__sai_aplus_handlers__ || []).push(handler);
    if (register) register(function () {
      var handlers = window.__sai_aplus_handlers__ || [];
      var idx = handlers.indexOf(handler);
      if (idx !== -1) handlers.splice(idx, 1);
    });
  }

  // Write ?variant=<id> deferred to a microtask so co-located click listeners
  // fire first. Guarded against a no-op replace. The patched history fans the
  // change out to every registered handler.
  function pushAplusVariantToUrl(variantId) {
    var url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    if (url.href === window.location.href) return;
    Promise.resolve().then(function () {
      history.replaceState({}, '', url.toString());
    });
  }

  // Single sanctioned add path. Routes through Spectrum.cart.addAndOpen when
  // present (opens the theme drawer + section refresh), else a plain
  // /cart/add.js POST through the patched window.fetch. EITHER way the edge cart
  // SDK's fetch interceptor is the SOLE dispatcher of the cart-updated event —
  // this never dispatches that event, never patches fetch, never fires parallel
  // ATC analytics (that would double-count attribution). Returns a promise
  // resolving to { ok:boolean }.
  function aplusCartAdd(variantId, quantity, sourceId) {
    var lines = [{ id: variantId, quantity: quantity }];
    if (window.Spectrum && window.Spectrum.cart && typeof window.Spectrum.cart.addAndOpen === 'function') {
      return Promise.resolve(window.Spectrum.cart.addAndOpen(lines, { sourceId: sourceId })).then(function (result) {
        if (result && result.ok === false) return { ok: false };
        return { ok: true };
      });
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: lines })
    }).then(function (res) {
      return { ok: !!res && res.ok };
    });
  }

  // Wire an ATC button: in-flight disabled + aria-busy, restore in a finally-like
  // path, visible error label swap on failure. preview projections no-op the
  // click (aria-disabled, no network, no fake success).
  function wireAplusAtcButton(button, module, getVariant, isPreview, sourceId) {
    if (!button) return;
    var atcLabel = module.getAttribute('data-aplus-cta-label') || 'Add to cart';
    var errorLabel = module.getAttribute('data-aplus-cta-error-label') || "Couldn't add — try again";
    if (isPreview) {
      button.setAttribute('aria-disabled', 'true');
    }
    button.addEventListener('click', function () {
      if (isPreview) return;
      if (button.getAttribute('aria-disabled') === 'true') return;
      var variant = getVariant();
      if (!variant || variant.available === false || variant.id == null) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      var quantity = readAplusQuantity(module);
      aplusCartAdd(variant.id, quantity, sourceId).then(function (result) {
        if (!result.ok) {
          button.textContent = errorLabel;
          if (window.console && window.console.warn) window.console.warn('[spectrum-aplus] add to cart failed');
          window.setTimeout(function () { button.textContent = atcLabel; }, 2400);
        }
      }).catch(function () {
        button.textContent = errorLabel;
        if (window.console && window.console.warn) window.console.warn('[spectrum-aplus] add to cart failed');
        window.setTimeout(function () { button.textContent = atcLabel; }, 2400);
      }).then(function () {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
    });
  }

  function readAplusQuantity(module) {
    var value = module.querySelector('.spectrum-aplus__buy-box-quantity-value');
    if (!value) return 1;
    var qty = parseInt(value.textContent || '1', 10);
    return isFinite(qty) && qty > 0 ? qty : 1;
  }

  // Reflect the resolved variant onto a module's own bind nodes: price,
  // compare-at (only when compareAtPrice > price in cents), availability, title,
  // and the ATC label/disabled state. Prices are bound from the preformatted
  // strings — NO client-side money formatting.
  function applyAplusVariantBinds(module, projection, variant) {
    var priceNode = module.querySelector('[data-aplus-bind="price"]');
    var compareNode = module.querySelector('[data-aplus-bind="compare-at"]');
    var availabilityNode = module.querySelector('[data-aplus-bind="availability"]');
    var titleNode = module.querySelector('[data-aplus-bind="title"]');
    var atcButton = module.querySelector('[data-aplus-action="atc"]');
    var soldOutLabel = module.getAttribute('data-aplus-cta-sold-out-label') || 'Sold out';
    var atcLabel = module.getAttribute('data-aplus-cta-label') || 'Add to cart';

    if (titleNode && !titleNode.textContent) titleNode.textContent = projection.title || '';

    if (!variant) {
      if (priceNode) priceNode.textContent = '';
      if (compareNode) compareNode.hidden = true;
      if (atcButton) { atcButton.disabled = true; atcButton.textContent = soldOutLabel; }
      return;
    }

    if (priceNode) priceNode.textContent = variant.priceFormatted || '';
    if (compareNode) {
      var showCompare = variant.compareAtPrice != null && variant.compareAtPrice > variant.price;
      if (showCompare) {
        compareNode.textContent = variant.compareAtPriceFormatted || '';
        compareNode.hidden = false;
      } else {
        compareNode.hidden = true;
      }
    }
    if (availabilityNode) availabilityNode.textContent = variant.available ? '' : soldOutLabel;
    if (atcButton) {
      if (variant.available) {
        atcButton.disabled = false;
        if (atcButton.getAttribute('aria-disabled') !== 'true') atcButton.textContent = atcLabel;
      } else {
        atcButton.disabled = true;
        atcButton.textContent = soldOutLabel;
      }
    }
  }

  function initBuyBoxes(root, register) {
    root.querySelectorAll('[data-aplus-module="buy-box"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var projection = readProductProjection();
      if (!projection) { degradeCommerceModule(module, 'no-product-data'); return; }
      var isPreview = projection.preview === true;
      var binder = bindListeners();

      var optionsHost = module.querySelector('[data-aplus-bind="option-groups"]');
      var selection = {};
      var options = projection.options || [];
      var currentVariant = currentVariantFromUrl(projection);

      // Seed selection from the current variant's options (or first option value).
      for (var oi = 0; oi < options.length; oi++) {
        var opt = options[oi];
        var pos = opt.position;
        var seeded = currentVariant ? currentVariant['option' + pos] : null;
        selection[pos] = seeded != null ? seeded : (opt.values && opt.values.length ? opt.values[0] : null);
      }

      function currentSelectionVariant() {
        return resolveAplusVariant(projection, selection) || currentVariant;
      }

      function refreshOptionUi() {
        var groups = module.querySelectorAll('.spectrum-aplus__buy-box-option-group');
        for (var gi = 0; gi < groups.length; gi++) {
          var group = groups[gi];
          var gpos = parseInt(group.getAttribute('data-aplus-option-position') || '0', 10);
          var pills = group.querySelectorAll('.spectrum-aplus__buy-box-option-value');
          for (var pi = 0; pi < pills.length; pi++) {
            var pill = pills[pi];
            var val = pill.getAttribute('data-aplus-option-value');
            var isSel = val === selection[gpos];
            pill.classList.toggle('spectrum-aplus__buy-box-option-value--selected', isSel);
            pill.setAttribute('aria-pressed', isSel ? 'true' : 'false');
          }
        }
      }

      function pickOption(position, value) {
        if (selection[position] === value) return;
        selection[position] = value;
        var variant = currentSelectionVariant();
        refreshOptionUi();
        applyAplusVariantBinds(module, projection, variant);
        if (variant && variant.id != null) pushAplusVariantToUrl(variant.id);
      }

      // Build option-group pills from the projection (option-groups is an empty
      // bind target server-side). Sold-out values get the sold-out class +
      // aria-disabled; click still selects (matches theme conventions) but ATC
      // stays gated by the resolved variant's availability.
      if (optionsHost && options.length) {
        for (var gi2 = 0; gi2 < options.length; gi2++) {
          var g = options[gi2];
          var groupEl = document.createElement('div');
          groupEl.className = 'spectrum-aplus__buy-box-option-group';
          groupEl.setAttribute('data-aplus-option-position', String(g.position));
          var labelEl = document.createElement('span');
          labelEl.className = 'spectrum-aplus__buy-box-option-label';
          labelEl.textContent = g.name || '';
          groupEl.appendChild(labelEl);
          var valuesEl = document.createElement('div');
          valuesEl.className = 'spectrum-aplus__buy-box-option-values';
          var values = g.values || [];
          for (var vi = 0; vi < values.length; vi++) {
            (function (position, value) {
              var pillEl = document.createElement('button');
              pillEl.type = 'button';
              pillEl.className = 'spectrum-aplus__buy-box-option-value';
              pillEl.setAttribute('data-aplus-option-value', value);
              pillEl.textContent = value;
              // A value is available when some available variant carries it at
              // this position.
              var available = false;
              var variants = projection.variants || [];
              for (var xi = 0; xi < variants.length; xi++) {
                if (variants[xi].available && variants[xi]['option' + position] === value) { available = true; break; }
              }
              if (!available) {
                pillEl.classList.add('spectrum-aplus__buy-box-option-value--sold-out');
                pillEl.setAttribute('aria-disabled', 'true');
              }
              binder.on(pillEl, 'click', function () { pickOption(position, value); });
              valuesEl.appendChild(pillEl);
            })(g.position, values[vi]);
          }
          groupEl.appendChild(valuesEl);
          optionsHost.appendChild(groupEl);
        }
      }

      // Quantity stepper (button-driven).
      var qtyValue = module.querySelector('.spectrum-aplus__buy-box-quantity-value');
      var qtyDec = module.querySelector('[data-aplus-action="qty-dec"]');
      var qtyInc = module.querySelector('[data-aplus-action="qty-inc"]');
      function setQuantity(next) {
        var q = next < 1 ? 1 : next;
        if (qtyValue) qtyValue.textContent = String(q);
      }
      if (qtyDec) binder.on(qtyDec, 'click', function () { setQuantity(readAplusQuantity(module) - 1); });
      if (qtyInc) binder.on(qtyInc, 'click', function () { setQuantity(readAplusQuantity(module) + 1); });

      var atcButton = module.querySelector('[data-aplus-action="atc"]');
      wireAplusAtcButton(atcButton, module, currentSelectionVariant, isPreview, 'aplus-buy-box');

      refreshOptionUi();
      applyAplusVariantBinds(module, projection, currentSelectionVariant());

      // Re-sync on external ?variant= changes (theme picker, vdc8x4kp, sibling
      // modules). String-normalize ids when reading the URL.
      var onVariantChange = function () {
        var params = new URLSearchParams(window.location.search);
        var raw = params.get('variant');
        if (!raw) return;
        var v = findVariantById(projection, raw);
        if (!v) return;
        for (var pi = 1; pi <= 3; pi++) {
          if (v['option' + pi] != null) selection[pi] = v['option' + pi];
        }
        refreshOptionUi();
        applyAplusVariantBinds(module, projection, v);
      };
      registerAplusVariantHandler(onVariantChange, register);
      if (register) register(function () { binder.teardown(); });
    });
  }

  function initProductGalleries(root, register) {
    root.querySelectorAll('[data-aplus-module="product-gallery"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var projection = readProductProjection();
      if (!projection) { degradeCommerceModule(module, 'no-product-data'); return; }
      var binder = bindListeners();

      var spotlightImg = module.querySelector('.spectrum-aplus__product-gallery-spotlight-image');
      var thumbsHost = module.querySelector('[data-aplus-bind="gallery-thumbs"]');
      var lightbox = module.querySelector('.spectrum-aplus__product-gallery-lightbox');
      var lightboxImg = module.querySelector('.spectrum-aplus__product-gallery-lightbox-image');
      var images = projection.images || [];
      if (!images.length || !spotlightImg) { degradeCommerceModule(module, 'no-product-data'); return; }

      var activeIndex = 0;

      // Swap the spotlight by ASSIGNING src/alt on the existing <img> — never
      // innerHTML with projection strings.
      function showImage(index) {
        var count = images.length;
        if (!count) return;
        activeIndex = ((index % count) + count) % count;
        var image = images[activeIndex];
        spotlightImg.src = image.src;
        spotlightImg.alt = image.alt || '';
        if (lightboxImg) { lightboxImg.src = image.src; lightboxImg.alt = image.alt || ''; }
        var thumbs = module.querySelectorAll('.spectrum-aplus__product-gallery-thumb');
        for (var i = 0; i < thumbs.length; i++) {
          thumbs[i].classList.toggle('spectrum-aplus__product-gallery-thumb--active', i === activeIndex);
        }
      }

      // Build thumbs (gallery-thumbs is an empty bind target server-side).
      if (thumbsHost) {
        for (var ti = 0; ti < images.length; ti++) {
          (function (index, image) {
            var thumb = document.createElement('img');
            thumb.className = 'spectrum-aplus__product-gallery-thumb';
            thumb.src = image.thumb || image.src;
            thumb.alt = image.alt || '';
            thumb.setAttribute('data-aplus-index', String(index));
            binder.on(thumb, 'click', function () { showImage(index); });
            thumbsHost.appendChild(thumb);
          })(ti, images[ti]);
        }
      }

      // Lightbox: a plain <div> overlay toggled by the hidden attribute, closed
      // on Escape / backdrop / close button. No <dialog>.
      var spotlight = module.querySelector('[data-aplus-action="lightbox-open"]');
      var closeBtn = module.querySelector('[data-aplus-action="lightbox-close"]');
      function openLightbox() {
        if (!lightbox) return;
        lightbox.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      }
      function closeLightbox() {
        if (!lightbox) return;
        lightbox.hidden = true;
        document.documentElement.style.overflow = '';
      }
      if (spotlight && lightbox) binder.on(spotlight, 'click', openLightbox);
      if (closeBtn) binder.on(closeBtn, 'click', closeLightbox);
      if (lightbox) {
        binder.on(lightbox, 'click', function (event) { if (event.target === lightbox) closeLightbox(); });
        binder.on(document, 'keydown', function (event) {
          if (event.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
        });
      }

      showImage(0);

      // Jump the spotlight to a variant's image on external ?variant= change.
      var onVariantChange = function () {
        var params = new URLSearchParams(window.location.search);
        var raw = params.get('variant');
        if (!raw) return;
        var variant = findVariantById(projection, raw);
        if (!variant || !variant.image) return;
        for (var i = 0; i < images.length; i++) {
          if (images[i].src === variant.image) { showImage(i); return; }
        }
      };
      registerAplusVariantHandler(onVariantChange, register);
      if (register) register(function () { binder.teardown(); });
    });
  }

  function initStickyBuyBars(root, register) {
    root.querySelectorAll('[data-aplus-module="sticky-buy-bar"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var projection = readProductProjection();
      if (!projection) { degradeCommerceModule(module, 'no-product-data'); return; }
      var isPreview = projection.preview === true;

      function currentVariant() { return currentVariantFromUrl(projection); }

      applyAplusVariantBinds(module, projection, currentVariant());
      var atcButton = module.querySelector('[data-aplus-action="atc"]');
      wireAplusAtcButton(atcButton, module, currentVariant, isPreview, 'aplus-sticky-buy-bar');

      var onVariantChange = function () {
        applyAplusVariantBinds(module, projection, currentVariant());
      };
      registerAplusVariantHandler(onVariantChange, register);
    });
  }

  function initExpandableCardRails(root) {
    // Each card in an Expandable Card Rail may opt-in to a modal action.
    // The renderer emits a <button data-aplus-action="modal"> and a sibling
    // <div class="spectrum-aplus__card-rail-modal" id="…" hidden>. Click the
    // button → toggle the modal's hidden flag and the button's aria-expanded.
    root.querySelectorAll('[data-aplus-module="expandable-card-rail"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';
      var cards = Array.prototype.slice.call(module.querySelectorAll('.spectrum-aplus__card-rail-card'));
      cards.forEach(function (card) {
        var trigger = card.querySelector('[data-aplus-action="modal"]');
        var modal = card.querySelector('.spectrum-aplus__card-rail-modal');
        if (!trigger || !modal) return;
        trigger.setAttribute('aria-controls', modal.id || '');
        var sync = function () {
          var open = !modal.hidden;
          trigger.setAttribute('aria-expanded', String(open));
        };
        sync();
        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          modal.hidden = !modal.hidden;
          sync();
        });
      });
    });
  }

  function initSpecSpotlights(root) {
    var BASE = 'spectrum-aplus__spec-spotlight';
    root.querySelectorAll('[data-aplus-module="spec-spotlight"]').forEach(function (module) {
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';

      var buttons = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-index]'));
      if (!buttons.length) return;
      var panels = Array.prototype.slice.call(module.querySelectorAll('.' + BASE + '-panel'));
      var overlays = Array.prototype.slice.call(module.querySelectorAll('.' + BASE + '-overlay'));
      var prevBtn = module.querySelector('[data-aplus-action="spec-prev"]');
      var nextBtn = module.querySelector('[data-aplus-action="spec-next"]');
      var closeBtn = module.querySelector('[data-aplus-action="spec-close"]');
      var hasDefault = panels.some(function (panel) {
        return panel.getAttribute('data-aplus-panel') === 'default';
      });
      var lastIndex = buttons.length - 1;
      var restState = hasDefault ? 'default' : '0';

      function updateArrows(active) {
        var idx = active === 'default' ? -1 : Number(active);
        if (prevBtn) prevBtn.style.display = idx <= 0 ? 'none' : '';
        if (nextBtn) nextBtn.style.display = idx === lastIndex ? 'none' : '';
      }

      function setActive(active) {
        module.setAttribute('data-aplus-active', String(active));
        module.classList.toggle(BASE + '--open', active !== restState);
        buttons.forEach(function (button) {
          var on =
            active !== 'default' && button.getAttribute('data-aplus-index') === String(active);
          var item = button.closest('.' + BASE + '-item');
          if (item) item.classList.toggle(BASE + '-item--active', on);
          button.setAttribute('aria-pressed', String(on));
        });
        panels.forEach(function (panel) {
          var key = panel.getAttribute('data-aplus-panel');
          var on = key === String(active);
          var panelPos = key === 'default' ? -1 : Number(key);
          var activePos = active === 'default' ? -1 : Number(active);
          panel.classList.toggle(BASE + '-panel--active', on);
          panel.classList.toggle(BASE + '-panel--passed', panelPos < activePos);
          panel.setAttribute('aria-hidden', String(!on));
          panel.querySelectorAll('video').forEach(function (video) {
            if (on) video.play && video.play().catch(function () {});
            else video.pause && video.pause();
          });
        });
        overlays.forEach(function (overlay) {
          overlay.classList.toggle(
            BASE + '-overlay--active',
            overlay.getAttribute('data-aplus-panel') === String(active),
          );
        });
        updateArrows(active);
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          var idx = button.getAttribute('data-aplus-index') || '0';
          var current = module.getAttribute('data-aplus-active');
          setActive(current === idx ? restState : idx);
        });
      });
      if (closeBtn) closeBtn.addEventListener('click', function () { setActive(restState); });
      if (prevBtn) prevBtn.addEventListener('click', function () {
        var current = module.getAttribute('data-aplus-active');
        var idx = current === 'default' ? 0 : Number(current);
        if (idx > 0) setActive(String(idx - 1));
      });
      if (nextBtn) nextBtn.addEventListener('click', function () {
        var current = module.getAttribute('data-aplus-active');
        var idx = current === 'default' ? -1 : Number(current);
        if (idx < lastIndex) setActive(String(idx + 1));
      });

      setActive(module.getAttribute('data-aplus-active') || restState);
    });
  }

  function initLayeredMarqueeReveals(root, register) {
    var BASE = 'spectrum-aplus__marquee-hero';
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-aplus-module="layered-marquee-hero"][data-aplus-reveal="true"]').forEach(function (module) {
      if (module.dataset.aplusRevealInit === 'true') return;
      module.dataset.aplusRevealInit = 'true';

      var heading = module.querySelector('.' + BASE + '-heading');
      if (heading && !heading.querySelector('.spectrum-aplus__reveal-char')) {
        var text = String(heading.textContent || '');
        heading.setAttribute('aria-label', text.trim());
        heading.textContent = '';
        var charIndex = 0;
        Array.from(text).forEach(function (char) {
          if (char === ' ') { heading.append(document.createTextNode(' ')); return; }
          var span = document.createElement('span');
          span.className = 'spectrum-aplus__reveal-char';
          span.setAttribute('aria-hidden', 'true');
          span.style.transitionDelay = (charIndex * 45) + 'ms';
          span.textContent = char;
          heading.append(span);
          charIndex++;
        });
      }

      module.classList.add(BASE + '--reveal-ready');

      function revealNow() { module.classList.add(BASE + '--revealed'); }
      if (reduced || !('IntersectionObserver' in window)) { revealNow(); return; }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { observer.disconnect(); revealNow(); }
        });
      }, { threshold: 0.25 });
      observer.observe(module);
      if (register) register(function () { observer.disconnect(); });
    });
  }

  // Pinned layered-marquee variant: scrubs a sticky 100vh stage while scrolling
  // the tall track, fading the foreground character in over the first third and
  // drawing the SVG signature via stroke-dash from sigStart→1 (Pulsar ZywOo).
  // Path lengths are measured once; getTotalLength is too costly to call per
  // frame. Pins via the shared applyScrollPin (fixed-position, overflow-immune).
  function initLayeredMarqueeSignatures(root, register, claimedBlockIds) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root
      .querySelectorAll('[data-aplus-module="layered-marquee-hero"][data-aplus-pinned="true"]')
      .forEach(function (module) {
        if (isClaimed(module, claimedBlockIds)) return;
        if (module.dataset.aplusPinInit === 'true') return;
        module.dataset.aplusPinInit = 'true';

        var track = module.querySelector('.spectrum-aplus__marquee-hero-track');
        var stage = module.querySelector('.spectrum-aplus__marquee-hero-stage');
        var character = module.querySelector('.spectrum-aplus__marquee-hero-character');
        var svg = module.querySelector('.spectrum-aplus__marquee-hero-signature');
        if (!track || !stage) return;

        var paths = svg
          ? Array.prototype.slice.call(svg.querySelectorAll('.spectrum-aplus__marquee-hero-sig-path'))
          : [];
        var sigStart = svg
          ? Math.max(0, Math.min(0.95, Number(svg.getAttribute('data-aplus-sig-start')) || 0.6))
          : 0.6;
        var sigFill = svg ? svg.getAttribute('data-aplus-sig-fill') !== 'false' : false;
        var sigColor = (svg && svg.getAttribute('data-aplus-sig-color')) || '#fff';

        var lengths = paths.map(function (path) {
          var len = 0;
          try { len = path.getTotalLength(); } catch (error) { len = 0; }
          path.style.strokeDasharray = String(len);
          path.style.strokeDashoffset = String(len);
          return len;
        });

        if (reduced) {
          if (character) character.style.opacity = '1';
          paths.forEach(function (path) {
            path.style.strokeDashoffset = '0';
            if (sigFill) path.style.fill = sigColor;
          });
          return;
        }

        module.classList.add('spectrum-aplus__marquee-hero--scrub');

        function update() {
          applyScrollPin(track, stage);
          var rect = track.getBoundingClientRect();
          var distance = Math.max(1, track.offsetHeight - window.innerHeight);
          var progress = Math.max(0, Math.min(1, rect.top <= 0 ? Math.abs(rect.top) / distance : 0));
          if (character) {
            var charPos = Math.min(1, progress / 0.3);
            character.style.opacity = String(charPos);
            character.style.transform = 'translateX(-50%) translateY(' + (1 - charPos) * 40 + 'px)';
          }
          if (paths.length) {
            var sigProgress = sigStart >= 1 ? 0 : Math.max(0, (progress - sigStart) / (1 - sigStart));
            sigProgress = Math.min(1, sigProgress);
            paths.forEach(function (path, index) {
              path.style.strokeDashoffset = String(lengths[index] * (1 - sigProgress));
              if (sigFill) path.style.fill = sigProgress >= 0.95 ? sigColor : 'transparent';
            });
          }
        }

        var ticking = false;
        var animationFrame = 0;
        function requestUpdate() {
          if (ticking) return;
          ticking = true;
          animationFrame = window.requestAnimationFrame(function () {
            ticking = false;
            animationFrame = 0;
            update();
          });
        }
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);
        if (register) register(function () {
          window.removeEventListener('scroll', requestUpdate);
          window.removeEventListener('resize', requestUpdate);
          if (animationFrame) window.cancelAnimationFrame(animationFrame);
        });
        update();
      });
  }

  // Element-level in-view motion (element-style motion leaf, inview-* tokens).
  // The hidden pre-reveal state only applies after JS arms the element
  // (data-aplus-motion-inview="ready"), so a no-JS storefront — or a theme
  // runtime fork that predates this initialiser — renders the element fully
  // visible. Reveal fires once per element, matching the module-level reveal
  // semantics; reduced-motion visitors reveal immediately without arming.
  function initElementMotionReveals(root, register) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('.sp-motion-inview-rise, .sp-m-motion-inview-rise, .sp-motion-inview-fade, .sp-m-motion-inview-fade, .sp-motion-inview-scale, .sp-m-motion-inview-scale').forEach(function (el) {
      if (el.dataset.aplusMotionInit === 'true') return;
      el.dataset.aplusMotionInit = 'true';

      function revealNow() { el.setAttribute('data-aplus-motion-inview', 'in'); }
      if (reduced || !('IntersectionObserver' in window)) { revealNow(); return; }

      el.setAttribute('data-aplus-motion-inview', 'ready');
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { observer.disconnect(); revealNow(); }
        });
      }, { threshold: 0.25 });
      observer.observe(el);
      if (register) register(function () { observer.disconnect(); });
    });
  }

  // Section entrance reveals (data-aplus-entrance). The hidden pre-reveal
  // state exists only under the runtime-added ready class, so a no-JS page
  // renders fully visible. Sections already inside the viewport at init are
  // never armed — they stand in place with no animation (revealing
  // already-painted content late is the jank that makes merchants disable
  // theme entrance toggles). A section inside a scene wrapper is
  // scene-claimed: the scene owns its motion (one driver per element), so
  // entrance never arms there.
  function initEntranceReveals(root, register, claimedBlockIds) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;
    var armed = [];
    root.querySelectorAll('[data-aplus-entrance]').forEach(function (section) {
      if (section.dataset.aplusEntranceInit === 'true') return;
      section.dataset.aplusEntranceInit = 'true';
      if (isClaimed(section, claimedBlockIds)) return;
      var rect = section.getBoundingClientRect();
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < viewportHeight && rect.bottom > 0) return;
      section.classList.add('spectrum-aplus-entrance-ready');
      armed.push(section);
    });
    if (!armed.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var section = entry.target;
        var repeats = section.getAttribute('data-aplus-entrance-repeat') === 'every-view';
        if (entry.isIntersecting) {
          section.classList.add('spectrum-aplus-entrance-in');
          if (!repeats) observer.unobserve(section);
        } else if (repeats) {
          section.classList.remove('spectrum-aplus-entrance-in');
        }
      });
    }, { threshold: 0.15 });
    armed.forEach(function (section) { observer.observe(section); });
    if (register) register(function () { observer.disconnect(); });
  }

  function initMetricCountUps(root, register) {
    if (!('IntersectionObserver' in window)) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-aplus-module="marquee-metrics-band"][data-aplus-animate-values="true"]').forEach(function (module) {
      if (module.dataset.aplusCountInit === 'true') return;
      module.dataset.aplusCountInit = 'true';
      var values = Array.prototype.slice.call(module.querySelectorAll('[data-aplus-count-up="true"]'));
      var targets = values.map(function (el) {
        var raw = String(el.textContent || '');
        var match = raw.match(/[0-9][0-9.,]*/);
        if (!match) return null;
        var numeric = parseFloat(match[0].replace(/,/g, ''));
        if (!isFinite(numeric)) return null;
        return {
          el: el,
          target: numeric,
          prefix: raw.slice(0, match.index),
          suffix: raw.slice(match.index + match[0].length),
          grouped: match[0].indexOf(',') !== -1,
          decimals: (match[0].split('.')[1] || '').length
        };
      }).filter(Boolean);
      if (!targets.length || reduced) return;

      function format(spec, val) {
        var fixed = val.toFixed(spec.decimals);
        if (spec.grouped) {
          var parts = fixed.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          fixed = parts.join('.');
        }
        return spec.prefix + fixed + spec.suffix;
      }

      var DURATION = 1400;
      var frame = 0;
      function run() {
        var start = 0;
        function step(ts) {
          if (!start) start = ts;
          var progress = Math.min(1, (ts - start) / DURATION);
          var eased = 1 - Math.pow(1 - progress, 3);
          targets.forEach(function (spec) { spec.el.textContent = format(spec, spec.target * eased); });
          if (progress < 1) frame = window.requestAnimationFrame(step);
          else targets.forEach(function (spec) { spec.el.textContent = format(spec, spec.target); });
        }
        frame = window.requestAnimationFrame(step);
      }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { observer.disconnect(); run(); }
        });
      }, { threshold: 0.4 });
      observer.observe(module);
      if (register) register(function () {
        observer.disconnect();
        if (frame) window.cancelAnimationFrame(frame);
      });
    });
  }

  function initParallaxHeroes(root, register, claimedBlockIds) {
    var strengthMap = { none: 0, slow: 24, medium: 56, strong: 96 };
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-aplus-module="parallax-hero"]').forEach(function (module) {
      if (isClaimed(module, claimedBlockIds)) return;
      if (module.dataset.aplusEnhanced === 'true') return;
      module.dataset.aplusEnhanced = 'true';
      var background = module.querySelector('.spectrum-aplus__parallax-hero-background');
      if (!background || reduced) return;

      // A strength of none maps to 0, so a bare "|| 56" fallback would wrongly
      // re-enable it — distinguish unset from an explicit 0.
      var mapped = strengthMap[module.getAttribute('data-aplus-strength')];
      var strength = mapped === undefined ? 56 : mapped;
      function isMobileStatic() {
        return module.getAttribute('data-aplus-mobile-mode') === 'static' && window.innerWidth <= 768;
      }

      function update() {
        if (isMobileStatic() || strength === 0) {
          background.style.transform = 'none';
          return;
        }
        var rect = module.getBoundingClientRect();
        var travel = window.innerHeight + rect.height;
        var progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / travel));
        var offset = (progress - 0.5) * strength;
        background.style.transform = 'translate3d(0,' + offset + 'px,0) scale(1.04)';
      }

      var ticking = false;
      var animationFrame = 0;
      function requestUpdate() {
        if (ticking) return;
        ticking = true;
        animationFrame = window.requestAnimationFrame(function () {
          ticking = false;
          animationFrame = 0;
          update();
        });
      }

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      if (register) register(function () {
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
      });
      update();
    });
  }

  // Single-source enhancement entry for both hosts. opts is optional:
  //   - register(cleanup): teardown sink for a re-rendering host (storefront
  //     variant swap). Absent on zero-config hosts → no-op.
  //   - claimedBlockIds: Set/array of scene-claimed section ids that suppresses
  //     scroll/entrance inits for scene-owned sections (storefront). Absent →
  //     the DOM [data-sp-motion-scene] wrapper is the claim signal (landing).
  // Scroll-driven and entrance inits are scene-claim-gated; interactivity
  // (videos, carousels, tabs, accordions, spotlights, expandable cards) always
  // runs — scenes own scroll motion, not interaction.
  function enhance(root, opts) {
    root = root || document;
    opts = opts || {};
    var register = typeof opts.register === 'function' ? opts.register : noopRegister;
    var claimedBlockIds = normalizeClaimedBlockIds(opts.claimedBlockIds);
    initInlineVideos(root, register);
    initHighlightsCarousels(root, register);
    initTabbedMedia(root, register);
    initScrollFeatureSelectors(root, register, claimedBlockIds);
    initScrollRevealSequences(root, register, claimedBlockIds);
    initScrollTextReveals(root, register, claimedBlockIds);
    initParallaxHeroes(root, register, claimedBlockIds);
    initLayeredMarqueeReveals(root, register);
    initLayeredMarqueeSignatures(root, register, claimedBlockIds);
    initMetricCountUps(root, register);
    initElementMotionReveals(root, register);
    initEntranceReveals(root, register, claimedBlockIds);
    initExpandableCardRails(root);
    initTechnicalSpecsAccordions(root);
    initSpecSpotlights(root);
    initProductGalleries(root, register);
    initBuyBoxes(root, register);
    initStickyBuyBars(root, register);
  }

  window.SpectrumAplusRuntime = window.SpectrumAplusRuntime || {};
  window.SpectrumAplusRuntime.enhance = enhance;
  onReady(function () { enhance(document); });
})();